import React, { useState, useEffect } from "react";
import { X, User, Phone, Mail, MapPin, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import apiClient from "@/utils/apiClient";
import { toast } from "sonner";

const ParentEditModal = ({ isOpen, onClose, parent, onChildClick }) => {
  const [loading, setLoading] = useState(false);
  const [children, setChildren] = useState([]);

  const [formData, setFormData] = useState({
    father_name: "",
    mother_name: "",
    phone: "",
    secondary_phone: "",
    email: "",
    address: "",
    local_guardian_name: "",
    local_guardian_phone: "",
  });

  useEffect(() => {
    if (parent) {
      const firstChild = parent.children?.[0];

      setFormData({
        father_name: firstChild?.father_name || parent.name || "",
        mother_name: firstChild?.mother_name || parent.mother_name || "",
        phone: firstChild?.primary_phone || parent.phone || "",
        secondary_phone: firstChild?.secondary_phone || "",
        email: firstChild?.parent_email || parent.email || "",
        address: firstChild?.address || parent.address || "",
        local_guardian_name: firstChild?.local_guardian_name || "",
        local_guardian_phone: firstChild?.local_guardian_phone || "",
      });

      setChildren(parent.children || []);
    }
  }, [parent]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.father_name?.trim()) {
      toast.error("Father's name is required");
      return;
    }

    if (!formData.phone?.trim()) {
      toast.error("Primary phone number is required");
      return;
    }

    // Phone number validation - exactly 10 digits
    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(formData.phone)) {
      toast.error("Primary phone number must be exactly 10 digits");
      return;
    }

    // Optional phone validation
    if (
      formData.secondary_phone &&
      !phoneRegex.test(formData.secondary_phone)
    ) {
      toast.error(
        "Secondary phone number must be exactly 10 digits if provided"
      );
      return;
    }

    if (
      formData.local_guardian_phone &&
      !phoneRegex.test(formData.local_guardian_phone)
    ) {
      toast.error(
        "Local guardian phone number must be exactly 10 digits if provided"
      );
      return;
    }

    if (!formData.email?.trim()) {
      toast.error("Email address is required");
      return;
    }

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    if (!formData.address?.trim()) {
      toast.error("Address is required");
      return;
    }

    setLoading(true);

    try {
      // Validate parent ID exists
      if (!parent?.id) {
        throw new Error("Parent ID is missing");
      }

      // Backend now handles updating both parent and all children in one call
      const response = await apiClient.put(`/v1/admin/parents/${parent.id}`, {
        name:
          formData.father_name && formData.mother_name
            ? `${formData.father_name} & ${formData.mother_name}`
            : formData.father_name || formData.mother_name || "",
        phone: formData.phone,
        email: formData.email,
        address: formData.address,
        father_name: formData.father_name,
        mother_name: formData.mother_name || "", // Ensure empty string instead of undefined
        secondary_phone: formData.secondary_phone || "",
        local_guardian_name: formData.local_guardian_name || "",
        local_guardian_phone: formData.local_guardian_phone || "",
      });

      toast.success("Parent information updated successfully");
      onClose();

    } catch (error) {
      console.error("Update error:", error);

      // Handle specific error cases
      if (error.response?.status === 404) {
        toast.error("Parent not found. It may have been deleted.");
      } else if (error.response?.status === 400) {
        toast.error(error.response.data?.detail || "Invalid data provided");
      } else if (error.response?.status === 409) {
        toast.error("Parent with this email or phone already exists");
      } else {
        toast.error(error.response?.data?.detail || "Failed to update parent");
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-2xl font-bold" style={{ color: "#2C5F7F" }}>
              Edit Parent Information
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <User className="w-5 h-5" />
                Parent Details (Editable)
              </h4>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Father Name *
                  </label>
                  <input
                    type="text"
                    value={formData.father_name}
                    onChange={(e) =>
                      handleChange("father_name", e.target.value)
                    }
                    placeholder="Enter father's full name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mother Name
                  </label>
                  <input
                    type="text"
                    value={formData.mother_name}
                    onChange={(e) =>
                      handleChange("mother_name", e.target.value)
                    }
                    placeholder="Enter mother's full name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Phone className="w-4 h-4 inline mr-1" />
                    Primary Phone *
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleChange("phone", e.target.value)}
                    placeholder="Enter 10-digit mobile number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Phone className="w-4 h-4 inline mr-1" />
                    Secondary Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.secondary_phone}
                    onChange={(e) =>
                      handleChange("secondary_phone", e.target.value)
                    }
                    placeholder="Alternate contact number (optional)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Mail className="w-4 h-4 inline mr-1" />
                    Email *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    placeholder="parent@example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <MapPin className="w-4 h-4 inline mr-1" />
                    Address *
                  </label>
                  <textarea
                    value={formData.address}
                    onChange={(e) => handleChange("address", e.target.value)}
                    rows={2}
                    placeholder="House/Flat No., Street, City, State - PIN Code"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Local Guardian Name
                  </label>
                  <input
                    type="text"
                    value={formData.local_guardian_name}
                    onChange={(e) =>
                      handleChange("local_guardian_name", e.target.value)
                    }
                    placeholder="Enter local guardian's name (if different)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Local Guardian Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.local_guardian_phone}
                    onChange={(e) =>
                      handleChange("local_guardian_phone", e.target.value)
                    }
                    placeholder="Enter 10-digit mobile number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            <div className="pt-6 border-t border-gray-200">
              <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Users className="w-5 h-5" />
                Children (Read-only - Click to view profile)
              </h4>

              {children.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No children assigned to this parent
                </p>
              ) : (
                <div className="space-y-3">
                  {children.map((child) => (
                    <div
                      key={child.id}
                      onClick={() => onChildClick(child.id)}
                      className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-blue-50 hover:border-blue-300 cursor-pointer transition-all"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900">
                            {child.name}
                          </p>
                          <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                            <span>Class: {child.class_name}</span>
                            {child.room_number && (
                              <span>Room: {child.room_number}</span>
                            )}
                            {child.blood_group && (
                              <span>Blood: {child.blood_group}</span>
                            )}
                          </div>
                        </div>
                        <div className="text-sm text-blue-600 hover:text-blue-800">
                          View Profile →
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </form>
        </div>

        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-end gap-4">
            <Button
              onClick={onClose}
              disabled={loading}
              className="bg-gray-500 hover:bg-gray-600 text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParentEditModal;
